import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const AttendanceQuickSync: React.FC = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const triggerQuickSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      setSyncError(null);

      const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_URL}/attendance/sync/trigger/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          force: false
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger sync');
      }

      const result = await response.json();

      if (result.status === 'success') {
        const data = result.data;
        setSyncResult(
          `✅ Sync completed! Students: ${data.students_synced || 0}, ` +
          `Teachers: ${data.teachers_synced || 0}, ` +
          `Attendance: ${(data.student_attendance_synced || 0) + (data.teacher_attendance_synced || 0)}`
        );
      } else if (result.status === 'warning') {
        const existing = result.existing_sync;
        setSyncResult(
          `⚠️ Already synced today! ` +
          `Students: ${existing?.students_synced || 0}, ` +
          `Teachers: ${existing?.teachers_synced || 0}, ` +
          `Records: ${existing?.attendance_records_synced || 0}`
        );
      } else {
        throw new Error(result.message || 'Sync failed');
      }

    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Daily Attendance Sync
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Sync today's attendance data from SIS
            </Typography>
          </Box>
          <Chip
            icon={<ScheduleIcon />}
            label="Auto: 6:00 AM"
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>

        {syncResult && (
          <Alert
            severity={syncResult.includes('⚠️') ? 'warning' : 'success'}
            sx={{ mb: 2 }}
            icon={syncResult.includes('⚠️') ? <ErrorIcon /> : <SuccessIcon />}
          >
            {syncResult}
          </Alert>
        )}

        {syncError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {syncError}
          </Alert>
        )}

        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={triggerQuickSync}
            disabled={syncing}
            fullWidth
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Last auto-sync: Today 06:00 AM (scheduled daily)
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AttendanceQuickSync;